import { useCallback } from 'react';

import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';

import { CostReportAllocationGroup } from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';

const ALLOCATION_GROUPS_QUERY_KEY = 'allocation-groups';

export const getAllocationGroupsQueryKey = (clusterIds?: string[]) =>
  [ALLOCATION_GROUPS_QUERY_KEY, clusterIds] as const;

// Only used here to invalidate co-dependent summary cache when groups load
const ALLOCATION_GROUPS_SUMMARY_QUERY_KEY = 'allocation-groups-summaries';

type UseAllocationGroupsQuery = {
  allocationGroups?: CostReportAllocationGroup[];
  isLoading: boolean;
  error?: unknown;
  refetch?: () => void;
  invalidate(): void;
};

const getAllocationGroupsQueryOptions = (
  clusterIds: string[] | undefined,
  enabled: boolean,
  queryClient: ReturnType<typeof useQueryClient>
) =>
  queryOptions({
    queryKey: getAllocationGroupsQueryKey(clusterIds),
    queryFn: async () => {
      const { data } = await apiClient.costReport.getAllocationGroups({
        clusterIds,
      });
      if (data.items?.length) {
        queryClient.invalidateQueries({
          queryKey: [ALLOCATION_GROUPS_SUMMARY_QUERY_KEY],
        });
      }
      return data.items || [];
    },
    enabled,
    refetchOnWindowFocus: false,
  });

export const useAllocationGroupsQuery = (
  clusterIds?: string[],
  enabled = true
): UseAllocationGroupsQuery => {
  const queryClient = useQueryClient();

  const {
    isLoading,
    data: allocationGroups,
    error,
    refetch,
  } = useQuery(
    getAllocationGroupsQueryOptions(clusterIds, enabled, queryClient)
  );

  const invalidate = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: getAllocationGroupsQueryKey(clusterIds),
      }),
    [queryClient, clusterIds]
  );

  return {
    allocationGroups,
    isLoading,
    error,
    refetch,
    invalidate,
  };
};
